import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid, Paper, Typography, LinearProgress } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import useNotification from '../../../../../core/hooks/use_notification';
import TextFieldFormik from '../../../../common/text_field';
import { SelectFieldFormik } from '../../../../common/select_field';
import { SubmitButton } from '../../../../common/button';
import BottomActionBar from "../../../../common/bottom_action_bar";

import { getProductSalesDataAction, getProductSalesDataState } from '../../../../../features/price/get_product_sales_data';
import { updateProductSalesDataAction, updateProductSalesDataState } from '../../../../../features/price/update_product_sales_data';
import { getDepositsState } from '../../../../../features/price/get_deposits';
import { getProductSalesDetailsNotificationData, updateProductSalesDetailsNotificationData } from "../../../../../core/utils/notification_utils";

const vatOptions = [
    { value: 0, label: '0%' },
    { value: 5, label: '5%' },
    { value: 8, label: '8%' },
    { value: 23, label: '23%' },
];

const ProductSalesDataForm = ({ productId }) => {
    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();

    const getProductSalesData = useSelector(getProductSalesDataState);
    const updateProductSalesData = useSelector(updateProductSalesDataState);
    const getDeposits = useSelector(getDepositsState);

    useEffect(() => {
        if (productId) {
            dispatch(getProductSalesDataAction(productId))
                .catch(response => showErrorMessage(getProductSalesDetailsNotificationData(response)));
        }
    }, [dispatch, showErrorMessage, productId]);

    if (getProductSalesData?.loading || !getProductSalesData?.data) {
        return <LinearProgress />;
    }

    const data = getProductSalesData.data;

    const initialValues = {
        defaultBasicSalesGrossPrice: data?.defaultBasicSalesGrossPrice ?? '',
        defaultPurchaseNetPrice: data?.defaultPurchaseNetPrice ?? '',
        vat: data?.vat ?? '',
        depositId: data?.deposit?.id ?? 'none'
    };

    const schema = Yup.object().shape({
        defaultBasicSalesGrossPrice: Yup.number().required('Cena brutto jest wymagana'),
        defaultPurchaseNetPrice: Yup.number().required('Cena netto jest wymagana'),
        vat: Yup.number().required('VAT jest wymagany')
    });

    const onSubmit = (values) => {
        const form = {
            ...values,
            depositId: values.depositId === 'none' ? null : values.depositId
        };

        dispatch(updateProductSalesDataAction({ productId, form }))
            .then(response => {
                showNotification(updateProductSalesDetailsNotificationData(response))
            })
    };

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }
        >
            <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>
                <Box sx={ { flexGrow: 1, mb: 4 } }>
                    <Paper variant="outlined" sx={ { p: 4 } }>
                        <Typography variant="subtitle2" color="textSecondary" sx={ { mb: 3, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' } }>
                            Parametry sprzedażowe (Waluta: { data?.currency || 'PLN' })
                        </Typography>

                        <Grid container spacing={ 3 }>
                            <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                                <TextFieldFormik name="defaultBasicSalesGrossPrice" label="Domyślna Cena Brutto Sprzedaży" type="number" required/>
                            </Grid>
                            <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                                <TextFieldFormik name="defaultPurchaseNetPrice" label="Cena Netto Zakupu" type="number" required/>
                            </Grid>
                            <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                                <SelectFieldFormik
                                    name="vat"
                                    label="Stawka VAT"
                                    options={ vatOptions }
                                    required
                                />
                            </Grid>
                            <Grid item xs={ 12 } sm={ 6 } md={ 3 }>
                                <SelectFieldFormik
                                    name="depositId"
                                    label="Kaucja"
                                    options={ [{
                                        value: 'none',
                                        label: 'Brak kaucji'
                                    }, ...getDeposits?.data?.map(deposit => ({
                                        value: deposit?.id,
                                        label: deposit?.name
                                    })) || []] }
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>

                <BottomActionBar>
                    <SubmitButton isLoading={ updateProductSalesData?.loading }>
                        Zapisz dane sprzedażowe
                    </SubmitButton>
                </BottomActionBar>
            </Box>
        </Formik>
    );
};

export default ProductSalesDataForm;